import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EyeIcon, CoinsIcon } from 'lucide-react';
type PlatformId = 'LinkedIn' | 'X' | 'Instagram' | 'YouTube' | 'TikTok';
const PLATFORMS: {
  id: PlatformId;
  slug: string;
}[] = [{
  id: 'LinkedIn',
  slug: 'linkedin'
}, {
  id: 'X',
  slug: 'x'
}, {
  id: 'Instagram',
  slug: 'instagram'
}, {
  id: 'YouTube',
  slug: 'youtube'
}, {
  id: 'TikTok',
  slug: 'tiktok'
}];

// The payout column (credits + cash value) is identical across every platform;
// only the VIEW thresholds and the worked "Example" change per platform.
const PAYOUTS = [{
  credits: '120,000',
  worthCredits: '$300',
  worthCash: '$200'
}, {
  credits: '360,000',
  worthCredits: '$900',
  worthCash: '$600'
}, {
  credits: '900,000',
  worthCredits: '$2,250',
  worthCash: '$1,500'
}, {
  credits: '3,000,000',
  worthCredits: '$7,500',
  worthCash: '$5,000'
}, {
  credits: '6,000,000',
  worthCredits: '$15,000',
  worthCash: '$10,000'
}];

// Per-platform view brackets (paired with PAYOUTS by index) plus the worked
// example shown beneath the table.
const PLATFORM_DATA: Record<PlatformId, {
  views: string[];
  example: string;
}> = {
  LinkedIn: {
    views: ['1,000 - 4,999', '5,000 - 14,999', '15,000 - 29,999', '30,000 - 99,999', '100,000+'],
    example: 'Your post hits 8,000 views. You earn $900 in credits or $600 paid out via Dub.'
  },
  X: {
    views: ['3,000 - 14,999', '15,000 - 29,999', '30,000 - 69,999', '70,000 - 299,999', '300,000+'],
    example: 'Your post hits 18,000 views. You earn $900 in credits or $600 paid out via Dub.'
  },
  Instagram: {
    views: ['3,000 - 14,999', '15,000 - 29,999', '30,000 - 199,999', '200,000 - 999,999', '1,000,000+'],
    example: 'Your post hits 22,000 views. You earn $900 in credits or $600 paid out via Dub.'
  },
  YouTube: {
    views: ['2,000 - 9,999', '10,000 - 29,999', '30,000 - 79,999', '80,000 - 299,999', '300,000+'],
    example: 'Your post hits 15,000 views. You earn $900 in credits or $600 paid out via Dub.'
  },
  TikTok: {
    views: ['3,000 - 9,999', '10,000 - 29,999', '30,000 - 69,999', '70,000 - 299,999', '300,000+'],
    example: 'Your post hits 22,000 views. You earn $900 in credits or $600 paid out via Dub.'
  }
};
export function CreatorEarnings() {
  const [activePlatform, setActivePlatform] = useState<PlatformId>('LinkedIn');
  const {
    views,
    example
  } = PLATFORM_DATA[activePlatform];
  return <section className="py-20 md:py-28 px-6 md:px-8 max-w-5xl mx-auto" data-id="element-2605" data-mp="2adeurupzduzu">
      <div className="text-center mb-12" data-id="element-2606" data-mp="26a8q2riiobvh">
        <h2 className="font-title font-bold text-4xl md:text-6xl tracking-tight mb-8 text-[#2B211A]" data-id="element-2607" data-mp="21j0dxnpsycem">
          See how much you can earn
        </h2>
      </div>

      <div className="max-w-4xl mx-auto" data-id="element-2608" data-mp="1wjngk91b1mxs">
        {/* Platform selector — real platform logos */}
        <div className="flex justify-center mb-12" data-id="element-2609" data-mp="2u0m7lo3h6sbt">
          <div className="inline-flex items-center gap-1 p-1.5 bg-white rounded-full border border-neutral-200 shadow-sm" data-id="element-2610" data-mp="2p81eypwb2l9v">
            {PLATFORMS.map(({
            id,
            slug
          }) => {
            const active = activePlatform === id;
            return <button key={id} type="button" onClick={() => setActivePlatform(id)} aria-label={id} aria-pressed={active} className={`relative flex items-center justify-center w-16 md:w-24 h-11 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#EA580C]`} data-id="element-2611" data-mp="31kwvxww4bxnv">
                  
                  {active && <motion.span layoutId="earnings-platform-pill" className="absolute inset-0 rounded-full bg-[#2B211A] shadow-md" transition={{
                type: 'spring',
                stiffness: 400,
                damping: 32
              }} data-id="element-2612" data-mp="3kheq0kyfw9na" />}
                  <img src={`https://cdn.simpleicons.org/${slug}/${active ? 'ffffff' : '9ca3af'}`} alt={id} className="relative z-10 w-5 h-5 object-contain" loading="lazy" onError={e => {
                const img = e.currentTarget as HTMLImageElement;
                if (!img.dataset.fallback) {
                  img.dataset.fallback = '1';
                  img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
                } else {
                  img.style.visibility = 'hidden';
                }
              }} data-id="element-2613" data-mp="3u6ok26cug2e1" />
                  
                </button>;
          })}
          </div>
        </div>

        {/* Column header */}
        <div className="grid grid-cols-3 gap-4 mb-4 px-6 items-center" data-id="element-2614" data-mp="36nmhvqe25ouu">
          <div className="flex items-center gap-2 text-xs font-bold text-neutral-400 uppercase tracking-wider" data-id="element-2615" data-mp="32lhs52qqdcdh">
            <EyeIcon size={14} strokeWidth={2.5} data-id="element-2616" data-mp="3gl0ybpzzedn5" />
            Views
          </div>
          <div className="flex items-center justify-center gap-2 text-xs font-bold text-neutral-400 uppercase tracking-wider" data-id="element-2617" data-mp="3bl4uprv74o84">
            <CoinsIcon size={14} strokeWidth={2.5} data-id="element-2618" data-mp="vtgvtqwmglmo" />
            Payout
          </div>
          <div className="text-right flex justify-end" data-id="element-2619" data-mp="qw8e1rfdyjly">
            <span className="bg-[#EA580C] text-white px-3 py-1 rounded-full text-xs font-bold" data-id="element-2620" data-mp="qxt87fxsg0ja">
              50% more with credits
            </span>
          </div>
        </div>

        {/* Tiers */}
        <AnimatePresence mode="wait" data-id="element-2621" data-mp="2dpsj5qa53rrs">
          <motion.div key={activePlatform} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.25
        }} className="space-y-4" data-id="element-2622" data-mp="1zt5fz5rlg5df">
            
            {PAYOUTS.map((payout, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.04
          }} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center bg-white rounded-2xl p-6 border border-neutral-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-2623" data-mp="2whv072qp1a2k" data-mpi={i}>
              
                <div className="font-title font-bold text-2xl text-[#2B211A]" data-id="element-2624" data-mp="3g86v8xehn3o7">
                  {views[i]}
                </div>
                <div className="text-center font-medium text-[#2B211A]" data-id="element-2625" data-mp="3q09m33u1m2e7">
                  {payout.credits} credits
                </div>
                <div className="text-right flex flex-col items-end justify-center" data-id="element-2626" data-mp="3g3n20h9jjfd8">
                  <span className="text-[#EA580C] font-medium text-sm" data-id="element-2627" data-mp="3b5v3h4ee4v02">
                    worth {payout.worthCredits}
                  </span>
                  <span className="text-neutral-400 text-sm" data-id="element-2628" data-mp="3752rw4i1qe0m">
                    or {payout.worthCash} in cash
                  </span>
                </div>
              </motion.div>)}
          </motion.div>
        </AnimatePresence>

        {/* Worked example */}
        <div className="mt-10 px-2" data-id="element-2629" data-mp="2sfn50zlmysq7">
          <p className="text-[#EA580C] font-bold text-sm mb-2" data-id="element-2630" data-mp="2sgos62fxf5oa">Example</p>
          <AnimatePresence mode="wait" data-id="element-2631" data-mp="2neppyvvxaesx">
            <motion.p key={activePlatform} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2
          }} className="text-neutral-500 leading-relaxed" data-id="element-2632" data-mp="2nhll6cgdypdo">
              
              {example}
            </motion.p>
          </AnimatePresence>
          <p className="text-[#2B211A] font-bold mt-2" data-id="element-2633" data-mp="17xcl34sbgyng">
            Same post. 50% more value with credits.
          </p>
        </div>
      </div>
    </section>;
}